import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, Permission } from '@barbeiropro/contracts';
import { api, HttpError } from './api';

export const meKey = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      try {
        return await api.get<MeResponse>('/auth/me');
      } catch (e) {
        if (e instanceof HttpError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/** Membership ativa + helpers de permissão. Só use dentro do AppLayout (sessão garantida). */
export function useSession() {
  const { data } = useMe();
  const active = data?.memberships.find((m) => m.organizationId === data.activeOrganizationId) ?? null;
  const perms = new Set<Permission>(active?.permissions ?? []);
  return {
    me: data ?? null,
    org: active,
    can: (p: Permission) => perms.has(p),
    barberId: active?.barberId ?? null,
  };
}

/** O operador definiu a senha deste usuário: até trocá-la, as telas do tenant redirecionam para Minha conta. Na impersonação nunca redireciona. */
export function needsPasswordChange(me: Pick<MeResponse, 'mustChangePassword' | 'impersonation'> | null | undefined): boolean {
  return Boolean(me?.mustChangePassword && !me.impersonation);
}

/** O usuário de tenant ainda não aceitou a versão vigente dos Termos/Política. Operador e impersonação nunca são barrados. */
export function needsTermsAcceptance(me: Pick<MeResponse, 'termsAccepted' | 'impersonation' | 'platformAdmin'> | null | undefined): boolean {
  return Boolean(me && me.termsAccepted === false && !me.impersonation && !me.platformAdmin);
}

/**
 * Para onde o AppLayout redireciona o usuário de tenant, ou null. A troca de senha tem prioridade:
 * primeiro Minha conta, depois o aceite dos termos.
 */
export function tenantGate(
  me: Pick<MeResponse, 'mustChangePassword' | 'termsAccepted' | 'impersonation' | 'platformAdmin'> | null | undefined,
  pathname: string,
): '/app/conta' | '/aceitar-termos' | null {
  if (needsPasswordChange(me)) return pathname === '/app/conta' ? null : '/app/conta';
  if (needsTermsAcceptance(me)) return '/aceitar-termos';
  return null;
}

export function useInvalidateSession() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: meKey });
}
